import React from "react";
import LegalPageShell from "./LegalPageShell";
import {
  BEENTARA_PRODUCT_SUMMARY,
  LEGAL_PRIVACY_EMAIL,
  LEGAL_SITE_URL,
} from "./legalContent";

export default function Privacy() {
  return (
    <LegalPageShell title="Privacy Policy">
      <h2>1. Introduction</h2>
      <p>
        This Privacy Policy explains how <strong>Beentara</strong> (“we”, “us”, “our”) collects,
        uses, stores, and protects information when you use our website at{" "}
        <a href={LEGAL_SITE_URL} target="_blank" rel="noopener noreferrer">
          beentara.com
        </a>
        , our admin panel, and related services (together, the “Platform”).
      </p>
      <p>
        {BEENTARA_PRODUCT_SUMMARY} By creating an account or using the Platform, you agree to the
        practices described in this policy.
      </p>

      <h2>2. Who this policy applies to</h2>
      <p>This policy applies to:</p>
      <ul>
        <li>
          <strong>Account holders</strong> — business owners, administrators, and staff who sign in
          to the Beentara admin panel.
        </li>
        <li>
          <strong>End users</strong> — individuals whose data you store in the Platform (for example
          customers, leads, students, or employees) as part of your business operations.
        </li>
      </ul>
      <p>
        If you are an admin user, you are responsible for ensuring that your use of the Platform and
        the data you upload complies with applicable laws and that you have appropriate notices in
        place for your own customers.
      </p>

      <h2>3. Information we collect</h2>
      <p>Depending on how you use Beentara, we may collect:</p>
      <ul>
        <li>
          <strong>Account information</strong> — name, email, phone, login credentials, role, and
          business or organisation details.
        </li>
        <li>
          <strong>Business and operational data</strong> — information you enter or generate in the
          Platform, including customers, orders, products, inventory, invoices, receipts, expenses,
          CRM leads, admissions, attendance, menus, settings, and activity logs.
        </li>
        <li>
          <strong>Payment-related information</strong> — billing references, transaction identifiers,
          and amounts processed through integrated payment providers. We do not store full card
          numbers on our servers; payment processing is handled by third-party gateways where
          applicable.
        </li>
        <li>
          <strong>Technical and usage data</strong> — IP address, browser type, device information,
          session data, log files, and actions taken within the admin panel (such as sign-in times
          and feature usage) to keep the service secure and reliable.
        </li>
        <li>
          <strong>Communications</strong> — messages you send to us for support, onboarding, or
          account queries.
        </li>
        <li>
          <strong>Files you upload</strong> — documents, images, or exports attached to records
          (for example customer documents, admission photos, or import files).
        </li>
      </ul>

      <h2>4. How we use your information</h2>
      <p>We use collected information to:</p>
      <ul>
        <li>Provide, operate, maintain, and improve the Beentara Platform.</li>
        <li>Authenticate users and enforce access controls for admin and staff accounts.</li>
        <li>Process transactions, invoices, receipts, and related business workflows you configure.</li>
        <li>Send service notifications, security alerts, OTPs, and account-related messages.</li>
        <li>Provide customer support and respond to your requests.</li>
        <li>Monitor performance, diagnose errors, prevent fraud, and protect the security of our systems.</li>
        <li>Comply with legal obligations and enforce our Terms &amp; Conditions.</li>
        <li>
          Send product updates or important service announcements (you may opt out of non-essential
          marketing where applicable).
        </li>
      </ul>

      <h2>5. Legal basis for processing</h2>
      <p>
        Where required by law, we process personal data based on one or more of the following: your
        consent; performance of a contract with you; our legitimate interests in operating and
        securing the Platform; and compliance with legal obligations.
      </p>

      <h2>6. How we share information</h2>
      <p>We do not sell your personal information. We may share data only in these circumstances:</p>
      <ul>
        <li>
          <strong>Service providers</strong> — hosting, cloud infrastructure, email/SMS delivery,
          payment processors, analytics, and security vendors who assist us under contractual
          obligations to protect your data.
        </li>
        <li>
          <strong>Within your organisation</strong> — data visible to other users on your account
          according to the roles and permissions you assign (Admin, Staff, SuperAdmin, etc.).
        </li>
        <li>
          <strong>Legal requirements</strong> — when required by law, court order, or government
          request, or to protect rights, safety, and integrity of Beentara and its users.
        </li>
        <li>
          <strong>Business transfers</strong> — in connection with a merger, acquisition, or sale
          of assets, subject to appropriate confidentiality safeguards.
        </li>
      </ul>

      <h2>7. Data retention</h2>
      <p>
        We retain information for as long as your account is active or as needed to provide the
        Platform, resolve disputes, enforce agreements, and meet legal or regulatory requirements.
        When you delete records within the admin panel, we process deletion according to our
        technical workflows and backup schedules. Residual copies may remain in secure backups for a
        limited period before being overwritten.
      </p>

      <h2>8. Security</h2>
      <p>
        We implement reasonable administrative, technical, and organisational measures to protect
        information against unauthorised access, alteration, disclosure, or destruction. These
        measures include access controls, encrypted connections (HTTPS), and authentication
        mechanisms such as passwords and optional two-step verification.
      </p>
      <p>
        No method of transmission or storage is completely secure. You are responsible for keeping
        your login credentials confidential and for configuring appropriate permissions for staff
        accounts.
      </p>

      <h2>9. Your rights and choices</h2>
      <p>Subject to applicable law, you may have the right to:</p>
      <ul>
        <li>Access, correct, or update personal information held about you.</li>
        <li>Request deletion of certain data, subject to legal and contractual limits.</li>
        <li>Object to or restrict certain processing activities.</li>
        <li>Withdraw consent where processing is based on consent.</li>
        <li>Request a copy of your data in a portable format where technically feasible.</li>
      </ul>
      <p>
        To exercise these rights, contact us at{" "}
        <a href={`mailto:${LEGAL_PRIVACY_EMAIL}`}>{LEGAL_PRIVACY_EMAIL}</a>. We may need to verify
        your identity before fulfilling a request.
      </p>

      <h2>10. Cookies and similar technologies</h2>
      <p>
        The admin panel and website may use cookies, local storage, and similar technologies to keep
        you signed in, remember preferences, and understand how the Platform is used. You can
        control cookies through your browser settings; disabling certain cookies may affect login
        and functionality.
      </p>

      <h2>11. Third-party links and integrations</h2>
      <p>
        The Platform may link to or integrate with third-party services (for example payment
        gateways, maps, or messaging providers). Their privacy practices are governed by their own
        policies. We encourage you to review those policies when you use integrated features.
      </p>

      <h2>12. Children&apos;s privacy</h2>
      <p>
        Beentara is a business operations platform intended for use by organisations and authorised
        adults. We do not knowingly collect personal information directly from children under 18 for
        consumer account registration. If your business processes student or minor data (for example
        through admissions modules), you must obtain appropriate parental or guardian consent as
        required by law.
      </p>

      <h2>13. International data transfers</h2>
      <p>
        Your information may be processed on servers located in India or other countries where we or
        our service providers operate. We take steps to ensure appropriate safeguards when data is
        transferred across borders, consistent with applicable law.
      </p>

      <h2>14. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. The “Last updated” date at the top of
        this page will reflect the latest version. Material changes may be communicated through the
        Platform or by email. Continued use after changes take effect constitutes acceptance of the
        updated policy.
      </p>

      <h2>15. Contact us</h2>
      <p>
        For privacy-related questions, requests, or complaints, contact:
      </p>
      <ul>
        <li>
          Email: <a href={`mailto:${LEGAL_PRIVACY_EMAIL}`}>{LEGAL_PRIVACY_EMAIL}</a>
        </li>
        <li>
          Website:{" "}
          <a href={LEGAL_SITE_URL} target="_blank" rel="noopener noreferrer">
            {LEGAL_SITE_URL}
          </a>
        </li>
      </ul>
    </LegalPageShell>
  );
}
